import React, { useState } from "react";
import { createRoute } from "@tanstack/react-router";
import { onboardingRootRoute } from "./onboarding-root";
import { tenantService } from "../../shared/api/tenant.service";
import { useOnboardingStore } from "../../shared/store";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Input,
  FormControl,
  Container,
  Alert,
  FormHelperText,
  Table,
  CircularProgress,
  Grid
} from "@mui/joy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

interface ProvisionConfig {
  productionTenantId: string;
  sandboxTenantId: string;
  previewTenantId: string;
}

interface ProvisionStatus {
  production: "pending" | "success" | "error" | null;
  sandbox: "pending" | "success" | "error" | null;
  preview: "pending" | "success" | "error" | null;
}

const ProvisionProdPage: React.FC = () => {
  const { uuid, tenantId } = useOnboardingStore();
  const [config, setConfig] = useState<ProvisionConfig>({
    productionTenantId: "",
    sandboxTenantId: "",
    previewTenantId: ""
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string>("");
  const [provisionStatus, setProvisionStatus] = useState<ProvisionStatus>({
    production: null,
    sandbox: null,
    preview: null
  });
  const [allSuccess, setAllSuccess] = useState(false);

  const handleInputChange = (field: keyof ProvisionConfig, value: string) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!config.productionTenantId.trim()) {
      newErrors.productionTenantId = "Production Tenant ID is required";
    }
    if (!config.sandboxTenantId.trim()) {
      newErrors.sandboxTenantId = "Sandbox Tenant ID is required";
    }
    if (!config.previewTenantId.trim()) {
      newErrors.previewTenantId = "Preview Tenant ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProvision = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setApiError("");
    setAllSuccess(false);

    try {
      if (!tenantId) {
        throw new Error(
          "Tenant ID not found. Please complete the onboarding process first."
        );
      }

      // Reset status
      setProvisionStatus({
        production: "pending",
        sandbox: "pending",
        preview: "pending"
      });

      // Call API to provision tenants
      const response = await tenantService.provisionProductionTenants({
        uuid: uuid || "",
        sourceTenantId: tenantId,
        productionTenantId: config.productionTenantId,
        sandboxTenantId: config.sandboxTenantId,
        previewTenantId: config.previewTenantId
      });

      // Update status based on response
      setProvisionStatus({
        production: response.production?.success ? "success" : "error",
        sandbox: response.sandbox?.success ? "success" : "error",
        preview: response.preview?.success ? "success" : "error"
      });

      // Check if all were successful
      const allSuccessful =
        response.production?.success &&
        response.sandbox?.success &&
        response.preview?.success;

      setAllSuccess(allSuccessful);

      if (!allSuccessful) {
        const failedEnvs = [];
        if (!response.production?.success) failedEnvs.push("Production");
        if (!response.sandbox?.success) failedEnvs.push("Sandbox");
        if (!response.preview?.success) failedEnvs.push("Preview");

        setApiError(
          `Failed to provision ${failedEnvs.join(", ")} environment(s). Support has been notified and will assist you.`
        );
      }
    } catch (error: any) {
      console.error("Error provisioning environments:", error);
      setApiError(
        error.message ||
          "Failed to provision environments. Support has been notified. Please contact support if the issue persists."
      );
      setProvisionStatus({
        production: "error",
        sandbox: "error",
        preview: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: ProvisionStatus[keyof ProvisionStatus]) => {
    if (status === "success") {
      return <CheckCircleOutlineIcon color="success" />;
    }if (status === "error") {
      return <ErrorOutlineIcon color="error" />;
    }if (status === "pending") {
      return <CircularProgress size="sm" />;
    }
    return null;
  };

  const getStatusText = (status: ProvisionStatus[keyof ProvisionStatus]) => {
    if (status === "success") return "Completed";
    if (status === "error") return "Failed";
    if (status === "pending") return "Processing...";
    return "Pending";
  };

  const getStatusColor = (status: ProvisionStatus[keyof ProvisionStatus]) => {
    if (status === "success") return "success";
    if (status === "error") return "danger";
    if (status === "pending") return "warning";
    return "neutral";
  };

  return (
    <Container sx={{ maxWidth: "1248px", width: "100%" }}>
      <Box sx={{ pt: 1.5, pb: 0 }}>
        <Typography level="h2" sx={{ mb: 1, textAlign: "center" }}>
          Environment Provisioning
        </Typography>

        <Alert sx={{ mb: 1 }}>
          <Typography level="body-sm">
            Create your production, sandbox, and preview environments based on
            your implementation environment.
          </Typography>
        </Alert>

        {apiError && (
          <Alert color="danger" sx={{ mb: 1 }}>
            {apiError}
          </Alert>
        )}

        {allSuccess && (
          <Alert color="success" sx={{ mb: 1 }}>
            <Typography level="body-sm">
              All environments have been successfully provisioned! You can now
              access your production environment.
            </Typography>
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ py: 2 }}>
                <Typography level="title-md" sx={{ mb: 1.5 }}>
                  Workday Tenant IDs
                </Typography>

                <Table
                  variant="outlined"
                  sx={{
                    "& thead th:nth-of-type(1)": { width: "40%" },
                    "& thead th:nth-of-type(2)": { width: "60%" }
                  }}
                >
                  <tbody>
                    <tr>
                      <td>
                        <Typography
                          level="body-md"
                          fontWeight="bold"
                          sx={{ textAlign: "left" }}
                        >
                          Production Tenant ID
                        </Typography>
                      </td>
                      <td>
                        <FormControl
                          error={!!errors.productionTenantId}
                          sx={{ width: "100%" }}
                        >
                          <Input
                            placeholder={
                              tenantId ? `e.g., ${tenantId}` : "e.g., mycompany"
                            }
                            value={config.productionTenantId}
                            onChange={(e) =>
                              handleInputChange(
                                "productionTenantId",
                                e.target.value
                              )
                            }
                            size="sm"
                            disabled={isLoading || allSuccess}
                            endDecorator={getStatusIcon(
                              provisionStatus.production
                            )}
                          />
                          {errors.productionTenantId && (
                            <FormHelperText>
                              {errors.productionTenantId}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography
                          level="body-md"
                          fontWeight="bold"
                          sx={{ textAlign: "left" }}
                        >
                          Sandbox Tenant ID
                        </Typography>
                      </td>
                      <td>
                        <FormControl
                          error={!!errors.sandboxTenantId}
                          sx={{ width: "100%" }}
                        >
                          <Input
                            placeholder={
                              tenantId
                                ? `e.g., ${tenantId}_sbx`
                                : "e.g., mycompany_sbx"
                            }
                            value={config.sandboxTenantId}
                            onChange={(e) =>
                              handleInputChange(
                                "sandboxTenantId",
                                e.target.value
                              )
                            }
                            size="sm"
                            disabled={isLoading || allSuccess}
                            endDecorator={getStatusIcon(
                              provisionStatus.sandbox
                            )}
                          />
                          {errors.sandboxTenantId && (
                            <FormHelperText>
                              {errors.sandboxTenantId}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <Typography
                          level="body-md"
                          fontWeight="bold"
                          sx={{ textAlign: "left" }}
                        >
                          Preview Tenant ID
                        </Typography>
                      </td>
                      <td>
                        <FormControl
                          error={!!errors.previewTenantId}
                          sx={{ width: "100%" }}
                        >
                          <Input
                            placeholder={
                              tenantId
                                ? `e.g., ${tenantId}_preview`
                                : "e.g., mycompany_preview"
                            }
                            value={config.previewTenantId}
                            onChange={(e) =>
                              handleInputChange(
                                "previewTenantId",
                                e.target.value
                              )
                            }
                            size="sm"
                            disabled={isLoading || allSuccess}
                            endDecorator={getStatusIcon(
                              provisionStatus.preview
                            )}
                          />
                          {errors.previewTenantId && (
                            <FormHelperText>
                              {errors.previewTenantId}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {/* Status Display */}
                {(provisionStatus.production ||
                  provisionStatus.sandbox ||
                  provisionStatus.preview) && (
                  <Box sx={{ mt: 2 }}>
                    <Typography level="title-sm" sx={{ mb: 1 }}>
                      Provisioning Status:
                    </Typography>
                    <Stack spacing={1}>
                      <Alert color={getStatusColor(provisionStatus.production)}>
                        <Typography level="body-sm">
                          Production:{" "}
                          {getStatusText(provisionStatus.production)}
                        </Typography>
                      </Alert>
                      <Alert color={getStatusColor(provisionStatus.sandbox)}>
                        <Typography level="body-sm">
                          Sandbox: {getStatusText(provisionStatus.sandbox)}
                        </Typography>
                      </Alert>
                      <Alert color={getStatusColor(provisionStatus.preview)}>
                        <Typography level="body-sm">
                          Preview: {getStatusText(provisionStatus.preview)}
                        </Typography>
                      </Alert>
                    </Stack>
                  </Box>
                )}

                <Button
                  onClick={handleProvision}
                  size="lg"
                  fullWidth
                  disabled={isLoading || allSuccess}
                  startDecorator={isLoading && <CircularProgress size="sm" />}
                  sx={{ mt: 2 }}
                >
                  {isLoading
                    ? "Provisioning environments..."
                    : allSuccess
                      ? "All Environments Provisioned"
                      : "Provision Environments"}
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Help Box */}
          <Grid xs={12} md={6}>
            <Card
              variant="soft"
              color="primary"
              sx={{ height: "100%", position: "sticky", top: 20 }}
            >
              <CardContent sx={{ py: 2 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoOutlinedIcon />
                    <Typography level="title-md">Setup Guide</Typography>
                  </Box>

                  <Stack spacing={1}>
                    <Typography level="title-sm">
                      Environment Provisioning
                    </Typography>
                    <Typography level="body-sm">
                      This process will create your production, sandbox, and
                      preview environments based on your implementation
                      environment configuration.
                    </Typography>

                    <Box component="div" sx={{ pl: 0, m: 0 }}>
                      <Typography level="body-sm" sx={{ mb: 0.5 }}>
                        <strong>What data will be copied?</strong>
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 1 }}>
                        The following data will be copied from your
                        implementation environment:
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 1 }}>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Email templates
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Settings and configurations
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Tags and permissions
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Conferencing settings
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Domain configurations
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography level="body-sm">
                            Workday mappings
                          </Typography>
                        </Box>
                      </Box>

                      <Typography level="body-sm" sx={{ mb: 0.5 }}>
                        <strong>What will NOT be copied?</strong>
                      </Typography>
                      <Typography level="body-sm" sx={{ mb: 1 }}>
                        Test data such as workers, user account connections,
                        preferences, locations, and roosterlinks will not be
                        copied to ensure a clean production environment.
                      </Typography>

                      <Typography level="body-sm" sx={{ mb: 0.5 }}>
                        <strong>Important:</strong>
                      </Typography>
                      <Box component="ul" sx={{ pl: 2, mt: 0.5, mb: 0.5 }}>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            The provisioning process takes a few minutes to
                            complete
                          </Typography>
                        </Box>
                        <Box component="li" sx={{ mb: 0.5 }}>
                          <Typography level="body-sm">
                            Please wait for the confirmation message before
                            navigating away
                          </Typography>
                        </Box>
                        <Box component="li">
                          <Typography level="body-sm">
                            If you encounter any issues, support will be
                            automatically notified
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Alert variant="soft" color="warning">
                      <Typography level="body-xs">
                        <strong>Need Help?</strong> If you encounter any issues
                        during the provisioning process, please contact support
                        at support@roosterinc.com for assistance.
                      </Typography>
                    </Alert>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export const provisionProdRoute = createRoute({
  getParentRoute: () => onboardingRootRoute,
  path: "/provision-prod",
  component: ProvisionProdPage
});
